import { ShoppingBag, Minus, Plus, Truck, Gift, ArrowRight, X, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

import { useCartStore } from "@/store/cartStore";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import confetti from "canvas-confetti";
import { formatPrice } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { fetchShopifyCollections, type ShopifyCollection } from "@/lib/shopify";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { SEO } from "@/components/SEO";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Flag imports
import flagIsrael from "@/assets/flag-israel.png";
import flagUSA from "@/assets/flag-usa.png";
import flagPalestine from "@/assets/flag-palestine.png";
import flagRussia from "@/assets/flag-russia.png";

// Font preview images
import fontA from "@/assets/fonts/font-a.webp";
import fontB from "@/assets/fonts/font-b.webp";
import fontC from "@/assets/fonts/font-c.webp";
import fontD from "@/assets/fonts/font-d.webp";
import fontE from "@/assets/fonts/font-e.webp";
import fontF from "@/assets/fonts/font-f.webp";

// Icon images
import iconHeart from "@/assets/icons/icon-heart.png";
import iconCrown from "@/assets/icons/icon-crown.png";
import iconStar from "@/assets/icons/icon-star.png";
import iconDavid from "@/assets/icons/icon-david.png";
import iconAmpersand from "@/assets/icons/icon-ampersand.png";

// Font and icon maps for cart display
const FONT_IMAGES: Record<string, string> = {
  "font-a": fontA,
  "font-b": fontB,
  "font-c": fontC,
  "font-d": fontD,
  "font-e": fontE,
  "font-f": fontF,
};

const ICON_IMAGES: Record<string, string> = {
  "heart": iconHeart,
  "crown": iconCrown,
  "star": iconStar,
  "david": iconDavid,
  "ampersand": iconAmpersand,
};

interface EmptyCartCategory {
  handle: string;
  title: string;
}

const FREE_SHIPPING_THRESHOLD = 500;
const CHECKOUT_LANG_STORAGE_KEY = 'cart-checkout-language';

// Language options for checkout with translations
const checkoutLanguages = [
  { code: 'he', label: 'עברית', flag: flagIsrael, dir: 'rtl' as const },
  { code: 'en', label: 'English', flag: flagUSA, dir: 'ltr' as const },
  { code: 'ar', label: 'العربية', flag: flagPalestine, dir: 'rtl' as const },
  { code: 'ru', label: 'Русский', flag: flagRussia, dir: 'ltr' as const },
];

// Cart page translations
const cartTranslations = {
  he: {
    pageTitle: 'סל הקניות שלך',
    breadcrumbHome: 'ראשי',
    breadcrumbCart: 'סל קניות',
    emptyTitle: 'הסל שלך ריק',
    emptyDescription: 'התחל לקנות ומצא את התכשיט המושלם עבורך',
    startShopping: 'התחל קנייה',
    continueShopping: 'המשך קנייה',
    freeShippingAchieved: 'הזמנתך זכאית למשלוח חינם! 🎉',
    freeShippingRemaining: (remaining: number) => `חסרים לך עוד ₪${Math.round(remaining).toLocaleString('he-IL')} למשלוח חינם`,
    subtotal: 'סיכום ביניים',
    total: 'סה"כ לתשלום',
    checkoutLanguage: 'שפת התשלום',
    checkout: 'המשך לתשלום',
    checkoutLoading: 'מעבר לתשלום...',
    product: 'מוצר',
    price: 'מחיר',
    quantity: 'כמות',
    itemTotal: 'סה"כ',
    engraving: 'חריטה',
    font: 'פונט',
    symbol: 'סמל',
    symbols: 'סמלים',
    includesSymbols: (count: number, price: number) => `כולל ${count} ${count === 1 ? 'סמל' : 'סמלים'} (+₪${price})`,
  },
  en: {
    pageTitle: 'Your Shopping Cart',
    breadcrumbHome: 'Home',
    breadcrumbCart: 'Cart',
    emptyTitle: 'Your cart is empty',
    emptyDescription: 'Start shopping and find your perfect jewelry',
    startShopping: 'Start Shopping',
    continueShopping: 'Continue Shopping',
    freeShippingAchieved: 'Your order qualifies for free shipping! 🎉',
    freeShippingRemaining: (remaining: number) => `You're ₪${Math.round(remaining).toLocaleString('en-US')} away from free shipping`,
    subtotal: 'Subtotal',
    total: 'Total',
    checkoutLanguage: 'Checkout Language',
    checkout: 'Proceed to Checkout',
    checkoutLoading: 'Redirecting...',
    product: 'Product',
    price: 'Price',
    quantity: 'Quantity',
    itemTotal: 'Total',
    engraving: 'Engraving',
    font: 'Font',
    symbol: 'Symbol',
    symbols: 'Symbols',
    includesSymbols: (count: number, price: number) => `Includes ${count} ${count === 1 ? 'symbol' : 'symbols'} (+₪${price})`,
  },
  ar: {
    pageTitle: 'سلة التسوق الخاصة بك',
    breadcrumbHome: 'الرئيسية',
    breadcrumbCart: 'السلة',
    emptyTitle: 'سلتك فارغة',
    emptyDescription: 'ابدأ التسوق واعثر على المجوهرات المثالية لك',
    startShopping: 'ابدأ التسوق',
    continueShopping: 'متابعة التسوق',
    freeShippingAchieved: 'طلبك مؤهل للشحن المجاني! 🎉',
    freeShippingRemaining: (remaining: number) => `ينقصك ₪${Math.round(remaining).toLocaleString('ar-EG')} للشحن المجاني`,
    subtotal: 'المجموع الفرعي',
    total: 'المجموع',
    checkoutLanguage: 'لغة الدفع',
    checkout: 'متابعة الدفع',
    checkoutLoading: 'جاري التحويل...',
    product: 'المنتج',
    price: 'السعر',
    quantity: 'الكمية',
    itemTotal: 'المجموع',
    engraving: 'النقش',
    font: 'الخط',
    symbol: 'رمز',
    symbols: 'رموز',
    includesSymbols: (count: number, price: number) => `يشمل ${count} ${count === 1 ? 'رمز' : 'رموز'} (+₪${price})`,
  },
  ru: {
    pageTitle: 'Ваша корзина',
    breadcrumbHome: 'Главная',
    breadcrumbCart: 'Корзина',
    emptyTitle: 'Ваша корзина пуста',
    emptyDescription: 'Начните покупки и найдите идеальное украшение для себя',
    startShopping: 'Начать покупки',
    continueShopping: 'Продолжить покупки',
    freeShippingAchieved: 'Ваш заказ получает бесплатную доставку! 🎉',
    freeShippingRemaining: (remaining: number) => `До бесплатной доставки осталось ₪${Math.round(remaining).toLocaleString('ru-RU')}`,
    subtotal: 'Промежуточный итог',
    total: 'Итого',
    checkoutLanguage: 'Язык оплаты',
    checkout: 'Перейти к оплате',
    checkoutLoading: 'Переход...',
    product: 'Товар',
    price: 'Цена',
    quantity: 'Количество',
    itemTotal: 'Итого',
    engraving: 'Гравировка',
    font: 'Шрифт',
    symbol: 'Символ',
    symbols: 'Символы',
    includesSymbols: (count: number, price: number) => `Включает ${count} ${count === 1 ? 'символ' : 'символов'} (+₪${price})`,
  },
};

const Cart = () => {
  const { items, updateQuantity, removeItem, createCheckout, isLoading, getTotalPrice, getItemKey } = useCartStore();
  const [emptyCartCategories, setEmptyCartCategories] = useState<EmptyCartCategory[]>([]);
  const [hasTriggeredConfetti, setHasTriggeredConfetti] = useState(false);
  
  // Initialize from localStorage with Hebrew as default
  const [selectedCheckoutLang, setSelectedCheckoutLang] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CHECKOUT_LANG_STORAGE_KEY) || 'he';
    }
    return 'he';
  });

  // Load empty cart categories from settings
  useEffect(() => {
    const loadEmptyCartCategories = async () => {
      try {
        const { data: setting } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'empty_cart_categories')
          .maybeSingle();

        if (!setting?.setting_value) return;

        const value = setting.setting_value as { handles?: string[] };
        const handles = value.handles || [];
        
        if (handles.length === 0) return;

        const { collections } = await fetchShopifyCollections(250);
        
        const categoriesWithTitles = handles.map((handle) => {
          const collection = collections.find(c => c.node.handle === handle);
          if (collection) {
            return { handle, title: collection.node.title };
          }
          const formattedTitle = handle
            .replace(/-/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
          return { handle, title: formattedTitle };
        });

        setEmptyCartCategories(categoriesWithTitles);
      } catch (error) {
        console.error('Error loading empty cart categories:', error);
      }
    };

    loadEmptyCartCategories();
  }, []);

  // Save to localStorage when language changes
  const handleLanguageChange = (langCode: string) => {
    setSelectedCheckoutLang(langCode);
    localStorage.setItem(CHECKOUT_LANG_STORAGE_KEY, langCode);
  };

  // Get current translations and direction based on selected checkout language
  const t = cartTranslations[selectedCheckoutLang as keyof typeof cartTranslations] || cartTranslations.he;
  const cartDir = checkoutLanguages.find(l => l.code === selectedCheckoutLang)?.dir || 'rtl';

  const totalPrice = getTotalPrice();
  const progress = Math.min((totalPrice / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const remaining = Math.max(FREE_SHIPPING_THRESHOLD - totalPrice, 0);
  const hasFreeShipping = totalPrice >= FREE_SHIPPING_THRESHOLD;

  // Track crossing the free-shipping threshold so confetti only fires once per "cart fill" cycle
  const prevHasFreeShippingRef = useRef<boolean>(hasFreeShipping);

  // Confetti: fire once when crossing the free shipping threshold
  useEffect(() => {
    // If cart is empty, reset and prepare for next cycle
    if (items.length === 0) {
      setHasTriggeredConfetti(false);
      prevHasFreeShippingRef.current = false;
      return;
    }

    const prevHasFreeShipping = prevHasFreeShippingRef.current;
    prevHasFreeShippingRef.current = hasFreeShipping;

    // Fire only on transition: false -> true
    if (hasFreeShipping && !prevHasFreeShipping && !hasTriggeredConfetti) {
      const confettiTimer = setTimeout(() => {
        setHasTriggeredConfetti(true);

        const colors = ["#a99670", "#d4c4a8", "#8b7355", "#c9b896"];
        const end = Date.now() + 2000;

        const frame = () => {
          confetti({
            particleCount: 3,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors,
          });
          confetti({
            particleCount: 3,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors,
          });

          if (Date.now() < end) requestAnimationFrame(frame);
        };

        frame();
      }, 500);

      return () => clearTimeout(confettiTimer);
    }
  }, [hasFreeShipping, items.length, hasTriggeredConfetti]);


  const handleCheckout = async () => {
    try {
      await createCheckout(selectedCheckoutLang);
    } catch (error) {
      console.error('Checkout error:', error);
    }
  };

  const ICON_PRICE = 60;

  return (
    <div className="min-h-screen flex flex-col" dir={cartDir}>
      <SEO
        title="סל קניות"
        description="סל הקניות שלך באליס תכשיטים - השלם את הרכישה ותיהנה ממשלוח חינם בהזמנות מעל 500 ש״ח"
        canonical="https://alice.jewelry/cart"
        noindex={true}
      />
      <Header />
      
      <main className="flex-1 pt-4 pb-8 md:pt-6 md:pb-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-12 max-w-6xl">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2 text-xs text-muted-foreground mb-6"
          >
            <Link to="/" className="hover:text-primary transition-colors">
              {t.breadcrumbHome}
            </Link>
            <ChevronLeft className={`h-3 w-3 ${cartDir === 'ltr' ? 'rotate-180' : ''}`} />
            <span className="text-foreground">{t.breadcrumbCart}</span>
          </motion.nav>

          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-2 mb-8"
          >
            <ShoppingBag className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <h1 className="text-xl md:text-2xl font-light text-foreground">
              {t.pageTitle}
            </h1>
          </motion.div>

          {items.length === 0 ? (
            /* Empty Cart State */
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <ShoppingBag className="h-16 w-16 text-primary/30 mb-6" strokeWidth={1} />
              <h2 className="text-lg font-light text-foreground mb-2">{t.emptyTitle}</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-md">{t.emptyDescription}</p>
              
              {/* Category buttons */}
              {emptyCartCategories.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 w-full max-w-xl">
                  {emptyCartCategories.map((category, index) => (
                    <motion.div
                      key={category.handle}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    >
                      <Link 
                        to={`/collections/${category.handle}`}
                        className="group relative overflow-hidden border border-primary/30 py-3 px-4 text-xs font-medium text-foreground transition-all duration-300 hover:border-primary hover:bg-primary/5 block text-center"
                      >
                        <span className="absolute inset-0 bg-primary/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-center" />
                        <span className="relative z-10">{category.title}</span>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}

              <Link to="/collections/all">
                <Button 
                  size="default"
                  className="px-8"
                >
                  {t.startShopping}
                </Button>
              </Link>
            </motion.div>
          ) : (
            /* Cart with Items */
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 lg:gap-6">
              {/* Items List */}
              <motion.div
                initial={{ opacity: 0, x: cartDir === 'rtl' ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="lg:col-span-2 space-y-3 order-2 lg:order-1"
              >
                {items.map((item, index) => {
                  const displayImage = item.variantImage?.url || item.product.images?.edges?.[0]?.node.url;
                  const displayAlt = item.variantImage?.altText || item.product.title;
                  const itemKey = getItemKey(item);
                  const basePrice = parseFloat(item.price.amount);
                  const iconsPrice = (item.icons?.length || 0) * ICON_PRICE;
                  const itemTotalPrice = (basePrice + iconsPrice) * item.quantity;

                  return (
                    <motion.div
                      key={itemKey}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      className="bg-card border border-border/50 p-3 md:p-4"
                    >
                      <div className="flex gap-3 md:gap-4">
                        {/* Product Image */}
                        <Link 
                          to={`/products/${item.product.handle}`}
                          className="w-20 h-20 md:w-24 md:h-24 bg-muted overflow-hidden flex-shrink-0 hover:opacity-90 transition-opacity"
                        >
                          {displayImage && (
                            <img
                              src={displayImage}
                              alt={displayAlt}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          )}
                        </Link>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <Link 
                                to={`/products/${item.product.handle}`}
                                className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-2"
                              >
                                {item.product.title}
                              </Link>
                              <p className="text-xs text-muted-foreground mt-0.5">{item.variantTitle}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => removeItem(itemKey)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>

                          {/* Price */}
                          <div className="mt-2">
                            <p className="text-sm text-primary font-semibold">
                              {formatPrice((basePrice + iconsPrice).toString(), item.price.currencyCode)}
                            </p>
                            {item.icons && item.icons.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {t.includesSymbols(item.icons.length, iconsPrice)}
                              </p>
                            )}
                          </div>

                          {/* Quantity Controls */}
                          <div className={`flex items-center gap-2 mt-3 ${cartDir === 'rtl' ? 'justify-end' : 'justify-start'}`}>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-xs font-medium w-6 text-center">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <span className={`text-sm font-semibold text-primary ${cartDir === 'rtl' ? 'mr-auto' : 'ml-auto'}`}>
                              {formatPrice(itemTotalPrice.toString(), item.price.currencyCode)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Engraving Details */}
                      {(item.engraving || (item.icons && item.icons.length > 0)) && (
                        <div className="mt-3 py-2 px-3 border border-primary/20 bg-primary/[0.02]">
                          <div className="flex items-center justify-center gap-3 text-xs flex-wrap">
                            {/* Engraving Text */}
                            {item.engraving && (
                              <>
                                {(item.engraving.texts && item.engraving.texts.length > 0) ? (
                                  item.engraving.texts.map((text, idx) => (
                                    <span key={idx} className="flex items-center gap-1">
                                      <span className="text-muted-foreground">{item.engraving!.texts.length > 1 ? `${t.engraving} ${idx + 1}:` : `${t.engraving}:`}</span>
                                      <span className="text-foreground">{text}</span>
                                    </span>
                                  ))
                                ) : (item.engraving as any).text ? (
                                  <span className="flex items-center gap-1">
                                    <span className="text-muted-foreground">{t.engraving}:</span>
                                    <span className="text-foreground">{(item.engraving as any).text}</span>
                                  </span>
                                ) : null}
                                
                                <span className="text-primary/30">|</span>
                                
                                <span className="flex items-center gap-1">
                                  <span className="text-muted-foreground">{t.font}:</span>
                                  {FONT_IMAGES[item.engraving.font] ? (
                                    <img 
                                      src={FONT_IMAGES[item.engraving.font]} 
                                      alt={item.engraving.font} 
                                      className="h-4 w-auto object-contain"
                                    />
                                  ) : (
                                    <span className="text-foreground">{item.engraving.font}</span>
                                  )}
                                </span>
                              </>
                            )}

                            {/* Icons */}
                            {item.icons && item.icons.length > 0 && (
                              <>
                                {item.engraving && <span className="text-primary/30">|</span>}
                                
                                <span className="flex items-center gap-1">
                                  <span className="text-muted-foreground">{item.icons.length === 1 ? `${t.symbol}:` : `${t.symbols}:`}</span>
                                  <span className="flex items-center gap-1">
                                    {item.icons.map((iconId, idx) => (
                                      ICON_IMAGES[iconId] ? (
                                        <img 
                                          key={idx}
                                          src={ICON_IMAGES[iconId]} 
                                          alt={iconId}
                                          className="h-4 w-4 object-contain"
                                        />
                                      ) : (
                                        <span key={idx} className="text-foreground">{iconId}</span>
                                      )
                                    ))}
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}

                {/* Continue Shopping Link */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                >
                  <Link 
                    to="/collections/all" 
                    className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowRight className={`h-3 w-3 ${cartDir === 'ltr' ? 'rotate-180' : ''}`} />
                    {t.continueShopping}
                  </Link>
                </motion.div>
              </motion.div>

              {/* Order Summary Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: cartDir === 'rtl' ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="lg:col-span-1 order-1 lg:order-2"
              >
                <div className="bg-card border border-border/50 p-3 md:p-4 lg:sticky lg:top-28 space-y-3 md:space-y-4">
                  {/* Free Shipping Progress */}
                  <div className="p-3 bg-primary/5 border border-primary/10">
                    <div className="flex items-center gap-2 mb-2">
                      {hasFreeShipping ? (
                        <Gift className="h-4 w-4 text-primary" />
                      ) : (
                        <Truck className="h-4 w-4 text-primary/70" />
                      )}
                      <span className="text-xs font-medium text-foreground">
                        {hasFreeShipping 
                          ? t.freeShippingAchieved 
                          : t.freeShippingRemaining(remaining)}
                      </span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>₪0</span>
                      <span>₪{FREE_SHIPPING_THRESHOLD}</span>
                    </div>
                  </div>

                  {/* Subtotal */}
                  <div className="flex justify-between items-center py-3 border-t border-border/50">
                    <span className="text-xs text-muted-foreground">{t.subtotal}</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatPrice(totalPrice.toString(), items[0]?.price.currencyCode || 'ILS')}
                    </span>
                  </div>

                  {/* Total */}
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-sm">{t.total}</span>
                    <span className="text-primary">
                      {formatPrice(totalPrice.toString(), items[0]?.price.currencyCode || 'ILS')}
                    </span>
                  </div>

                  {/* Language Selector */}
                  <div className="pt-3 border-t border-border/50">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-xs text-muted-foreground">{t.checkoutLanguage}</span>
                      <TooltipProvider delayDuration={100}>
                        <div className="flex gap-1">
                          {checkoutLanguages.map((lang) => (
                            <Tooltip key={lang.code}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleLanguageChange(lang.code)}
                                  className={`p-0.5 border-2 transition-all duration-200 ${
                                    selectedCheckoutLang === lang.code
                                      ? 'border-primary bg-primary/10'
                                      : 'border-transparent hover:border-primary/30'
                                  }`}
                                >
                                  <img
                                    src={lang.flag}
                                    alt={lang.label}
                                    className="w-6 h-4 object-cover"
                                  />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{lang.label}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </TooltipProvider>
                    </div>

                    {/* Checkout Button */}
                    <Button
                      className="w-full"
                      size="default"
                      onClick={handleCheckout}
                      disabled={isLoading}
                    >
                      {isLoading ? t.checkoutLoading : t.checkout}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      <Footer2 />
    </div>
  );
};

export default Cart;
